import { useState, useEffect } from 'react';
import logger from 'utils/logger';

import intl from 'react-intl-universal';
import { Modal, Box, Typography, Button, Stack } from '@mui/material';
import Grid from '@mui/material/Grid2';
import Card from '@mui/material/Card';
import Autocomplete from '@mui/material/Autocomplete';

import FormField from 'components/FormField';
import FormFieldHelperText from 'components/FormFieldHelperText';
import pxToRem from 'assets/theme/functions/pxToRem';
import {
  updateUserAttributes,
  setUpTOTP,
  getCurrentUser,
  verifyTOTPSetup,
  updateMFAPreference,
  fetchUserAttributes,
} from 'aws-amplify/auth';
import { QRCodeSVG } from 'qrcode.react';
import { ConfirmModal } from 'shared/modals/confirmModal';
import { MFA_TYPE } from 'shared/constants/mfa';
import { modalBorderRadius } from 'utils/styles';
import { useSnackbar } from 'notistack';

// Create a logger instance for this component
const log = logger.createLogger('MFATab');

const getPreferredMfaTypeOptions = () => {
  return [
    {
      label: intl.get('personal_settings_mfa_options_no_mfa'),
      value: MFA_TYPE.NOMFA,
    },
    {
      label: intl.get('personal_settings_mfa_options_totp'),
      value: MFA_TYPE.TOTP,
    },
    {
      label: intl.get('personal_settings_mfa_options_email'),
      value: MFA_TYPE.EMAIL,
    },
  ];
};

const DashboardPersonalSettingsMFA = () => {
  const [currentAuthenticatedUser, setCurrentAuthenticatedUser] = useState();
  const [showConfirmBeforeSend, setShowConfirmBeforeSend] = useState(false);
  const [userAttributesFormState, setUserAttributesFormState] = useState({
    preferredMfaType: MFA_TYPE.EMAIL,
  });
  const { enqueueSnackbar } = useSnackbar();
  const [secret, setSecret] = useState('');
  const [showTOTPSetting, setShowTOTPSetting] = useState(false);
  const [formState, setFormState] = useState({
    totpCode: '',
  });
  const [otpSetupCompleted, setOtpSetupCompleted] = useState(false);

  useEffect(() => {
    const fetchUser = async () => {
      try {
        const user = await getCurrentUser();
        const userAttributes = await fetchUserAttributes();
        const loggedUser = {
          ...userAttributes,
          ...user,
        };
        setCurrentAuthenticatedUser(loggedUser);
        if (userAttributes['custom:preferredMfaType']) {
          setUserAttributesFormState({
            preferredMfaType: userAttributes['custom:preferredMfaType'],
          });
        }
      } catch (error) {
        log.error('Error occurred', { error });
      }
    };
    fetchUser();
  }, []);

  const onOneTimePasswordChange = (key) => (event) => {
    setFormState((prevState) => ({
      ...prevState,
      [key]: event.target.value,
    }));
  };

  const changeMFAType = (key, newValue) => {
    setUserAttributesFormState((prevState) => ({
      ...prevState,
      [key]: newValue,
    }));
  };

  const handleSetTOTPClick = async (_) => {
    try {
      const { sharedSecret } = await setUpTOTP();
      setSecret(sharedSecret);
      setShowTOTPSetting(true);
    } catch (error) {
      log.error('Error occurred', { error });
    }
  };

  const handleUpdateClick = async () => {
    setShowConfirmBeforeSend(true);
  };

  const handleConfirmClick = async () => {
    setShowConfirmBeforeSend(false);
    sendToServer();
  };

  const handleVerifyTotpCode = async () => {
    try {
      await verifyTOTPSetup({
        code: formState.totpCode,
        sharedSecret: secret,
        userPoolId: import.meta.env.VITE_AWS_COGNITO_USER_POOL_ID,
      });
      await updateMFAPreference({ totp: 'PREFERRED' });
      await updateUserAttributes({
        userAttributes: {
          'custom:preferredMfaType': userAttributesFormState.preferredMfaType,
        },
      });
      setOtpSetupCompleted(true);
      setShowTOTPSetting(false);
      enqueueSnackbar(
        intl.get('personal_settings_mfa_alert_success_totp_setup'),
        {
          variant: 'success',
          autoHideDuration: 3000,
        }
      );
    } catch (error) {
      log.error('Error occurred', { error });
      enqueueSnackbar(
        intl.get('personal_settings_mfa_alert_text_wrong_totp_code'),
        {
          variant: 'error',
          autoHideDuration: 3000,
        }
      );
    }
  };

  const generateConfirmWindowContents = () => {
    const getPreferredMfaType = (type) => {
      switch (type) {
        case MFA_TYPE.NOMFA:
          return intl.get('personal_settings_mfa_options_no_mfa');
        case MFA_TYPE.TOTP:
          return intl.get('personal_settings_mfa_options_totp');
        case MFA_TYPE.EMAIL:
          return intl.get('personal_settings_mfa_options_email');
        default:
          return '';
      }
    };

    return (
      <Grid spacing={3} container style={{ marginTop: '-10px' }}>
        <Grid size={12}>
          <Typography variant="h6">
            {intl.get('personal_settings_mfa_label_preferred_mfa_type')}:{' '}
            {getPreferredMfaType(
              currentAuthenticatedUser['custom:preferredMfaType']
            ) +
              ' ==> ' +
              getPreferredMfaType(userAttributesFormState.preferredMfaType)}
          </Typography>
        </Grid>
      </Grid>
    );
  };

  const sendToServer = async () => {
    try {
      await updateUserAttributes({
        userAttributes: {
          'custom:preferredMfaType': userAttributesFormState.preferredMfaType,
        },
      });
      await updateMFAPreference({
        totp: otpSetupCompleted ? 'PREFERRED' : 'DISABLED',
      });
      enqueueSnackbar(
        intl.get('personal_settings_mfa_alert_success_mfa_type_update'),
        {
          variant: 'success',
          autoHideDuration: 3000,
        }
      );
    } catch (error) {
      log.error('Error occurred', { error });
    }
  };

  return (
    <>
      <Stack
        spacing={3}
        sx={{
          margin: '32px auto 0',
          width: '100%',
          maxWidth: '500px',
        }}
        alignItems="stretch">
        <Box mt={8} sx={{ minWidth: '500px' }}>
          <Autocomplete
            id="select_preferred_mfa_type"
            value={
              getPreferredMfaTypeOptions().filter(
                (type) =>
                  type.value === userAttributesFormState.preferredMfaType
              )[0]
            }
            onChange={(event, newValue) => {
              if (newValue && newValue.value)
                changeMFAType('preferredMfaType', newValue.value);
            }}
            isOptionEqualToValue={(option, value) =>
              option.value === value.value
            }
            options={getPreferredMfaTypeOptions()}
            renderInput={(params) => (
              <FormField
                {...params}
                variant="outlined"
                label={intl.get(
                  'personal_settings_mfa_label_preferred_mfa_type'
                )}
                InputLabelProps={{ shrink: true }}
              />
            )}
            disableClearable
          />
          <FormFieldHelperText>
            {intl.get('personal_settings_mfa_helper_select_preferred_mfa_type')}
          </FormFieldHelperText>
        </Box>

        <Button
          variant="contained"
          color="secondary"
          fullWidth
          disabled={userAttributesFormState.preferredMfaType !== MFA_TYPE.TOTP}
          onClick={handleSetTOTPClick}
          style={{
            color: 'white',
            fontSize: pxToRem(18),
            backgroundColor: '#54C6B8',
          }}>
          {intl.get('personal_settings_mfa_button_set_totp')}
        </Button>

        <Button
          variant="contained"
          color="primary"
          disabled={userAttributesFormState.preferredMfaType === MFA_TYPE.TOTP}
          onClick={handleUpdateClick}
          style={{
            color: 'white',
            fontSize: pxToRem(18),
          }}>
          {intl.get('personal_settings_mfa_button_update')}
        </Button>
      </Stack>

      <ConfirmModal
        isOpened={showConfirmBeforeSend}
        onCancel={() => setShowConfirmBeforeSend(false)}
        onConfirm={handleConfirmClick}
        title={intl.get('personal_settings_mfa_modal_text_confirm_title')}
        bodyText={intl.get('personal_settings_mfa_modal_text_confirm_hint')}
        confirmButtonText={intl.get(
          'personal_settings_mfa_modal_button_submit'
        )}
        cancelButtonText={intl.get(
          'personal_settings_mfa_modal_button_cancel'
        )}>
        <Box>
          <Grid
            spacing={3}
            container
            justifyContent="flex-end"
            alignItems="flex-end">
            <Grid size={12}>
              {showConfirmBeforeSend && generateConfirmWindowContents()}
            </Grid>
          </Grid>
        </Box>
      </ConfirmModal>

      {/* Setting OTP  */}
      <Modal
        open={showTOTPSetting}
        onClose={() => setShowTOTPSetting(false)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
        <Card sx={{ width: 800, borderRadius: modalBorderRadius.sm }}>
          <Box py={3} px={3} mt={3} mx={3}>
            <Stack spacing={3} alignItems="center">
              <Typography variant="h6">
                {intl.get('personal_settings_mfa_text_scan_qr_code')}
              </Typography>

              {currentAuthenticatedUser && secret && (
                <QRCodeSVG
                  value={`otpauth://totp/DoCloud:${currentAuthenticatedUser.username}?secret=${secret}&issuer=DoCloud`}
                />
              )}

              <Typography mb={1} variant="h6">
                {intl.get('personal_settings_mfa_text_input_qr_code')}
              </Typography>

              <Box mt={0} sx={{ minWidth: '500px' }}>
                <FormField
                  id="TOTP code"
                  name="TOTP code"
                  label={intl.get('personal_settings_mfa_label_totp_code')}
                  variant="outlined"
                  fullWidth
                  value={formState.totpCode}
                  inputProps={{ maxLength: 6 }}
                  onChange={onOneTimePasswordChange('totpCode')}
                />
              </Box>
              <Grid container spacing={5} justifyContent="space-between">
                <Grid>
                  <Button
                    variant="contained"
                    color="primary"
                    style={{
                      color: 'white',
                      fontSize: pxToRem(16),
                    }}
                    onClick={handleVerifyTotpCode}>
                    {intl.get('personal_settings_mfa_modal_button_submit')}
                  </Button>
                </Grid>

                <Grid>
                  <Button
                    variant="contained"
                    color="error"
                    style={{
                      fontSize: pxToRem(16),
                      color: 'white',
                      backgroundColor: 'red',
                    }}
                    onClick={() => {
                      setShowTOTPSetting(false);
                    }}>
                    {intl.get('personal_settings_mfa_modal_button_cancel')}
                  </Button>
                </Grid>
              </Grid>
            </Stack>
          </Box>
        </Card>
      </Modal>
    </>
  );
};

export default DashboardPersonalSettingsMFA;
